import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Lock, Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import PageTransition from "@/components/site/PageTransition";
import VinylLoader from "@/components/site/VinylLoader";

export default function DeliverableView() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [unlocked, setUnlocked] = useState(null);
  const [pass, setPass] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/deliverables/${slug}`).then((r) => setD(r.data)).catch(() => setD(false));
  }, [slug]);

  const unlock = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/deliverables/${slug}/unlock`, { passkey: pass });
      setUnlocked(data);
      toast.success("Unlocked.");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Invalid passkey"); }
    finally { setBusy(false); }
  };

  if (d === null) return <div className="pt-40 flex justify-center"><VinylLoader size={80} label="Loading deliverable" /></div>;
  if (d === false) return <div className="pt-40 text-center text-zinc-400" data-testid="deliv-not-found">Deliverable not found.</div>;

  return (
    <PageTransition>
      <div className="pt-32 pb-20 px-6 lg:px-10" data-testid="deliverable-view">
        <div className="max-w-5xl mx-auto">
          <div className="label-mono mb-3">/ Project Deliverable</div>
          <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tighter">{d.title}</h1>
          <p className="mt-6 text-zinc-300 text-lg max-w-3xl leading-relaxed">{d.synopsis}</p>

          {d.trailer_youtube_id && (
            <div className="mt-12">
              <div className="sunken-bloom" />
              <div className="sunken-frame" style={{ aspectRatio: "16/9" }}>
                <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${d.trailer_youtube_id}`} frameBorder="0" allow="autoplay" />
              </div>
            </div>
          )}

          {d.artwork_urls?.length > 0 && (
            <div className="mt-16">
              <div className="label-mono mb-4">/ Artwork</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {d.artwork_urls.map((u, i) => (
                  <div key={i} className="rounded-xl overflow-hidden border border-white/5"><img src={u} alt="" className="w-full object-cover"/></div>
                ))}
              </div>
            </div>
          )}

          {d.metadata && (
            <div className="mt-16 p-6 rounded-2xl border border-white/5 bg-[#121215]">
              <div className="label-mono mb-3">/ Metadata</div>
              <pre className="text-zinc-300 text-sm whitespace-pre-wrap font-mono">{d.metadata}</pre>
            </div>
          )}

          <div className="mt-16 p-8 rounded-3xl border border-white/10 bg-[#0c0c0e]">
            <div className="flex items-center gap-2 mb-4"><Lock size={18} className="text-cyan-400"/><div className="font-display text-xl font-bold tracking-tight">Restricted Downloads</div></div>
            {!unlocked ? (
              <form onSubmit={unlock} className="flex gap-3 max-w-md" data-testid="deliv-unlock-form">
                <input className="pulse-input" placeholder="Enter passkey" required value={pass} onChange={(e) => setPass(e.target.value)} data-testid="deliv-pass" />
                <button disabled={busy} className="glass-btn solid" data-testid="deliv-unlock-submit">Unlock</button>
              </form>
            ) : (
              <div className="space-y-3" data-testid="deliv-unlocked">
                {unlocked.pitch_deck_url && (
                  <a href={unlocked.pitch_deck_url} target="_blank" rel="noopener noreferrer" className="glass-btn"><FileText size={14}/> Pitch Deck <Download size={12}/></a>
                )}
                {unlocked.download_urls.map((u, i) => (
                  <a key={i} href={u} target="_blank" rel="noopener noreferrer" className="glass-btn block w-fit"><Download size={14}/> {u.split("/").pop() || `File ${i+1}`}</a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </PageTransition>
  );
}
